import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { AudioDataService } from '../../services/audio-data.service';
import { AudioData } from '../../models/audio-data';

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss']
})
export class AudioPlayerComponent implements OnChanges, AfterViewInit {
  data: AudioData = {} as AudioData;

  @Input() stopAudioPlayer: boolean = false;
  @ViewChild('audio') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('timelineContainer') timelineContainer!: ElementRef;
  @ViewChild('volumeContainer') volumeContainer!: ElementRef;
  @ViewChild('volumeSliderMD') volumeSliderMD!: ElementRef;
  @ViewChild('volumeSliderSM') volumeSliderSM!: ElementRef;
  
  totalTime: string = "0:00";
  currentTime: string = "0:00";
  volumeLevel: string = "high";
  currentTimeline: string = "";
  
  isPlay: boolean = false;
  wasPaused: boolean = false;
  isScrubbing: boolean = false;
  showSongTitle: boolean = false;
  showSingerTitle: boolean = false;
  showVolumeSlider: boolean = false;
  showAudioInfo: boolean = false;
  leadingZeroFormatter = new Intl.NumberFormat(undefined, {minimumIntegerDigits: 2});

  constructor(private audioDataService: AudioDataService) { };

  ngOnChanges(): void {
    if(this.stopAudioPlayer) this.turnOffPlayer();
  }

  ngAfterViewInit(): void {
    this.audioDataService.currentData.subscribe({
      next: data => {
        this.data.index = data?.index!;
        this.data.queue = data?.queue!;
        this.playAudio();
      }
    });
  }

  playAudio(): void {
    if(this.audioPlayer && this.data && this.data.queue){
      this.audioPlayer.nativeElement.src = this.data.queue[this.data.index].src;
      this.audioPlayer.nativeElement.load();
      this.audioPlayer.nativeElement.play();
      this.isPlay = false;
    } else if(!this.data) this.turnOffPlayer();
  }
  
  // Loaded Preview
  bufferedAudio(): void {
    if(!this.audioPlayer.nativeElement) return;
    let timeLoaded = 0;
    let timeTotal = 0;
    if(timeTotal === 0) timeTotal = this.audioPlayer.nativeElement.duration;
    if(this.audioPlayer.nativeElement.buffered.length === 1){
      timeLoaded = this.audioPlayer.nativeElement.buffered.end(0);
      this.timelineContainer.nativeElement.style.setProperty("--preview-position", `${(timeLoaded / timeTotal) * 100}%`);
    }
  }

  // Player Controls
  togglePlay(): void {
    this.audioPlayer.nativeElement.paused ? this.audioPlayer.nativeElement.play() : this.audioPlayer.nativeElement.pause();
  }

  stepBackward(): void {
    this.data.index -= 1;
    this.audioDataService.updateAudio(this.data);
    this.playAudio();
  }

  stepForward(): void {
    this.data.index += 1;
    this.audioDataService.updateAudio(this.data);
    this.playAudio();
  }

  playFromQueue(index: number): void {
    this.data.index = index;
    this.audioDataService.updateAudio(this.data);
    this.playAudio();
  }

  // Duration
  loadedDuration(): void {
    this.totalTime = this.formatDuration(this.audioPlayer.nativeElement.duration);
  }

  formatDuration(time: number): string {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor(time / 60) % 60;
    const hours = Math.floor(time / 3600);
    if(hours === 0) return `${minutes}:${this.leadingZeroFormatter.format(seconds)}`;
    else return `${hours}:${this.leadingZeroFormatter.format(minutes)}:${this.leadingZeroFormatter.format(seconds)}`;
  }

  updateDuration(): void {
    this.currentTime = this.formatDuration(this.audioPlayer.nativeElement.currentTime);
    const percent = this.audioPlayer.nativeElement.currentTime / this.audioPlayer.nativeElement.duration;
    this.timelineContainer?.nativeElement.style.setProperty("--progress-position", `${percent * 100}%`);
  }

  // Timeline
  handleTimelineUpdate(event: any): void {
    const rect = this.timelineContainer.nativeElement.getBoundingClientRect();
    const percent: number = Math.min(Math.max(0, event.x - rect.x), rect.width) / rect.width;
    this.timelineContainer.nativeElement.style.setProperty("--preview-position", `${percent * 100}%`);
    this.currentTimeline = this.formatDuration(percent * this.audioPlayer.nativeElement.duration);
    if(this.isScrubbing){
      event.preventDefault();
      this.timelineContainer.nativeElement.style.setProperty("--progress-position", `${percent * 100}%`);
    }
  }

  toggleScrubbing(event: any): void {
    const rect = this.timelineContainer.nativeElement.getBoundingClientRect();
    const percent = Math.min(Math.max(0, event.x - rect.x), rect.width) / rect.width;
    this.isScrubbing = (event.buttons & 1) === 1;
    this.timelineContainer.nativeElement.classList.toggle("scrubbing", this.isScrubbing);
    if(this.isScrubbing){
      this.wasPaused = this.audioPlayer.nativeElement.paused;
      this.audioPlayer.nativeElement.pause();
    } else {
      this.audioPlayer.nativeElement.currentTime = percent * this.audioPlayer.nativeElement.duration;
      if(!this.wasPaused) this.audioPlayer.nativeElement.play();
    }
    this.handleTimelineUpdate(event);
  }

  @HostListener('window:mouseup', ['$event'])
  updateScrubbing(event: any): void {
    if(this.isScrubbing) this.toggleScrubbing(event);
  }

  @HostListener('window:mousemove', ['$event'])
  updateTimeline(event: any): void {
    if(this.isScrubbing) this.handleTimelineUpdate(event);
  }

  // Volume Controls
  toggleMute(): void {
    this.audioPlayer.nativeElement.muted = !this.audioPlayer.nativeElement.muted;
  }

  @HostListener('window:resize', ['$event'])
  slideVolume(): void {
    if (this.volumeSliderMD || this.volumeSliderSM) {
      if(window.innerWidth > 768){
        this.audioPlayer.nativeElement.volume = this.volumeSliderMD.nativeElement.value;
        this.audioPlayer.nativeElement.muted = this.volumeSliderMD.nativeElement.value === 0;
        this.volumeSliderSM.nativeElement.style.display = "none";
      } else {
        this.audioPlayer.nativeElement.volume = this.volumeSliderSM.nativeElement.value;
        this.audioPlayer.nativeElement.muted = this.volumeSliderSM.nativeElement.value === 0;
        this.volumeSliderMD.nativeElement.style.display = "none";
      }
    }
  }

  changeVolume(): void {
    this.volumeSliderMD.nativeElement.value = this.audioPlayer.nativeElement.volume;
    this.volumeSliderSM.nativeElement.value = this.audioPlayer.nativeElement.volume;
    if(this.audioPlayer.nativeElement.muted || this.audioPlayer.nativeElement.volume === 0){
      this.volumeSliderMD.nativeElement.value = 0;
      this.volumeSliderSM.nativeElement.value = 0;
      this.volumeLevel = "muted";
    } else if(this.audioPlayer.nativeElement.volume >= .5) this.volumeLevel = "high";
    else this.volumeLevel = "low";
    this.volumeContainer.nativeElement.dataset.volumeLevel = this.volumeLevel;
  }

  // Download
  downloadAudio(): void {
    const link = document.createElement('a');
    link.href = this.audioPlayer.nativeElement.currentSrc;
    link.download = this.data.queue[this.data.index].song;
    link.target = "_blank";
    link.click();
  }

  // Turn Off Player
  turnOffPlayer(): void {
    this.isPlay = false;
    this.audioPlayer.nativeElement.pause();
    this.audioDataService.updateAudio(null);
    this.audioDataService.updateAudioStatus(false);
  }

  // Ended Audio
  endedAudio(): void {
    if(this.data.index + 1 === this.data.queue?.length) this.turnOffPlayer();
    else this.stepForward();
  }
}
